/**
 * §5 finance math — a faithful mirror of the backend `FinanceCalcService` (annuity present-value
 * formula, both directions). Kept client-side so the calculator updates instantly, exactly as
 * `lib/conditions.ts` mirrors the backend ConditionHelper. The backend service stays the authority
 * for server-side validation (credit review / booking gate, §6).
 */
export type PaymentFrequency = 'MONTHLY' | 'QUARTERLY';

export const periodsPerYear = (frequency: PaymentFrequency): number => (frequency === 'QUARTERLY' ? 4 : 12);

export const periodicRate = (annualRatePct: number, frequency: PaymentFrequency): number =>
	annualRatePct / 100 / periodsPerYear(frequency);

const round = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** Max loan a given per-period installment can service over `tenorYears`. */
export function maxLoan(installment: number, annualRatePct: number, tenorYears: number, frequency: PaymentFrequency): number {
	const n = tenorYears * periodsPerYear(frequency);
	const i = periodicRate(annualRatePct, frequency);
	if (n <= 0 || installment <= 0) return 0;
	const principal = i === 0 ? installment * n : (installment * (1 - Math.pow(1 + i, -n))) / i;
	return round(principal);
}

export interface MaxLoanRow {
	tenorYears: number;
	periods: number;
	maxLoan: number;
}

/** The DBR "Loan Amount per Tenor" table. */
export function maxLoanTable(
	installment: number,
	annualRatePct: number,
	tenorsYears: number[],
	frequency: PaymentFrequency,
): MaxLoanRow[] {
	return tenorsYears.map((tenorYears) => ({
		tenorYears,
		periods: tenorYears * periodsPerYear(frequency),
		maxLoan: maxLoan(installment, annualRatePct, tenorYears, frequency),
	}));
}

/** Per-period installment required to amortise `loanAmount` over `tenorYears`. */
export function installmentFor(loanAmount: number, annualRatePct: number, tenorYears: number, frequency: PaymentFrequency): number {
	const n = tenorYears * periodsPerYear(frequency);
	const i = periodicRate(annualRatePct, frequency);
	if (n <= 0 || loanAmount <= 0) return 0;
	const payment = i === 0 ? loanAmount / n : (loanAmount * i) / (1 - Math.pow(1 + i, -n));
	return round(payment);
}

export interface PaymentPlanResult {
	downPayment: number;
	loanAmount: number;
	installment: number;
	maxLoanForTenor: number | null;
	withinInstallment: boolean | null;
	withinDbr: boolean | null;
}

/** Derive down payment + loan + installment and check the plan fits the DBR allowance. */
export function evaluatePaymentPlan(input: {
	cashPrice: number;
	tenorYears: number;
	annualRatePct: number;
	frequency: PaymentFrequency;
	downPaymentPct?: number;
	availableInstallment?: number;
}): PaymentPlanResult {
	const downPaymentPct = input.downPaymentPct ?? 20;
	const downPayment = round((input.cashPrice * downPaymentPct) / 100);
	const loanAmount = round(input.cashPrice - downPayment);
	const installment = installmentFor(loanAmount, input.annualRatePct, input.tenorYears, input.frequency);
	const maxLoanForTenor =
		input.availableInstallment === undefined
			? null
			: maxLoan(input.availableInstallment, input.annualRatePct, input.tenorYears, input.frequency);
	return {
		downPayment,
		loanAmount,
		installment,
		maxLoanForTenor,
		withinInstallment: input.availableInstallment === undefined ? null : installment <= input.availableInstallment,
		withinDbr: maxLoanForTenor === null ? null : loanAmount <= maxLoanForTenor,
	};
}

/**
 * §DBR-override — canonical submission keys for the DBR calculator widget's editable inputs (ported
 * from workflow-manager-frontend/src/lib/finance.ts, 2026-08-05; BE twin: finance-facts.helper
 * DBR_CAPTURE_KEYS). The step form persists widget edits into the draft under these keys; the server
 * gives the carrying step's own capture precedence over the authored income mapping, so a saved edit
 * drives the enforced DBR (widget table, payment-plan fit check, completion guard) alike.
 */
export const DBR_CAPTURE_KEYS = {
	income1: 'DBR Calculator.Monthly Income',
	existing1: 'DBR Calculator.Existing Installments',
	income2: 'DBR Calculator.Monthly Income (Client 2)',
	existing2: 'DBR Calculator.Existing Installments (Client 2)',
	incomeJoint: 'DBR Calculator.Monthly Income (Joint)',
	existingJoint: 'DBR Calculator.Existing Installments (Joint)',
	incomeGuarantor: 'DBR Calculator.Monthly Income (Guarantor)',
	existingGuarantor: 'DBR Calculator.Existing Installments (Guarantor)',
} as const;

export type DbrCaptureField = keyof typeof DBR_CAPTURE_KEYS;

/** §Per-persona: the persona vocabulary of the DBR widget + each persona's capture fields. */
export const DBR_PERSONAS: {key: string; label: string; income: DbrCaptureField; existing: DbrCaptureField}[] = [
	{key: 'PRIMARY', label: 'Primary', income: 'income1', existing: 'existing1'},
	{key: 'JOINT', label: 'Joint', income: 'incomeJoint', existing: 'existingJoint'},
	{key: 'GUARANTOR', label: 'Guarantor', income: 'incomeGuarantor', existing: 'existingGuarantor'},
];

/** §DBR-surface: every section / column key — the default when a placement configures nothing. */
export const DBR_ALL_SECTIONS = ['income', 'available', 'tenorTable'];
export const DBR_ALL_TENOR_COLUMNS = ['tenor', 'rate', 'maxLoan'];

/** §Per-persona (WS7): a served persona breakdown entry, resolved onto its capture fields. */
export interface DbrPersonaSlot {
	key: string;
	label: string;
	incomeField: DbrCaptureField;
	existingField: DbrCaptureField;
	income: number;
	existing: number;
}

/** Unknown persona keys are dropped rather than guessed; empty ⇒ the legacy 2-slot layout applies. */
export function resolvePersonaSlots(
	personas?: {key: string; income: number; existing: number}[],
): DbrPersonaSlot[] {
	return (personas ?? [])
		.map(persona => {
			const meta = DBR_PERSONAS.find(candidate => candidate.key === persona.key);
			return meta
				? {
						key: meta.key,
						label: meta.label,
						incomeField: meta.income,
						existingField: meta.existing,
						income: persona.income,
						existing: persona.existing,
					}
				: null;
		})
		.filter((persona): persona is DbrPersonaSlot => persona !== null);
}

export interface DbrSums {
	income: number;
	existing: number;
	available: number;
}

/** §DBR-parity: available = income × threshold% − existing facilities (persona slots when configured,
 * else the legacy pair). `productObligations` is the bureau product-bucket total — additive, never
 * editable here. */
export function dbrSums(args: {
	values: Record<string, number>;
	personaSlots: DbrPersonaSlot[];
	joint: boolean;
	productObligations?: number;
	thresholdPct: number;
}): DbrSums {
	const {values, personaSlots, joint, thresholdPct} = args;
	const personaMode = personaSlots.length > 0;
	const personaIncome = personaSlots.reduce((sum, persona) => sum + (values[persona.incomeField] ?? 0), 0);
	const legacyIncome = (values.income1 ?? 0) + (joint ? (values.income2 ?? 0) : 0);
	const income = personaMode ? personaIncome : legacyIncome;
	const personaExisting = personaSlots.reduce((sum, persona) => sum + (values[persona.existingField] ?? 0), 0);
	const legacyExisting = (values.existing1 ?? 0) + (joint ? (values.existing2 ?? 0) : 0);
	const existing = (personaMode ? personaExisting : legacyExisting) + (args.productObligations ?? 0);
	return {income, existing, available: Math.max(0, income * (thresholdPct / 100) - existing)};
}

/** The server-resolved DBR inputs both finance widgets receive. */
export interface ServedDbrInputs {
	income1: number;
	existing1: number;
	income2: number;
	existing2: number;
	productObligations?: number;
	personas?: {key: string; income: number; existing: number}[];
}

/**
 * §DBR-override: the available installment recomputed from the LIVE draft values — the same formula
 * the widget and the server use — so the payment plan's DBR-fit reacts to an income edit as the agent
 * types. Each slot falls back to ITS server-resolved value.
 */
export function liveDbrAvailable(args: {
	liveFacts: Record<string, unknown>;
	dbrInputs: ServedDbrInputs;
	thresholdPct: number;
	asNumber: (value: unknown) => number | undefined;
}): number {
	const {liveFacts, dbrInputs, thresholdPct, asNumber} = args;
	const liveOr = (field: DbrCaptureField, fallback: number): number =>
		asNumber(liveFacts[DBR_CAPTURE_KEYS[field]]) ?? fallback;
	const personaSlots = resolvePersonaSlots(dbrInputs.personas);
	const values: Record<string, number> = personaSlots.length
		? Object.fromEntries(
				personaSlots.flatMap(persona => [
					[persona.incomeField, liveOr(persona.incomeField, persona.income)],
					[persona.existingField, liveOr(persona.existingField, persona.existing)],
				]),
			)
		: {
				income1: liveOr('income1', dbrInputs.income1),
				income2: liveOr('income2', dbrInputs.income2),
				existing1: liveOr('existing1', dbrInputs.existing1),
				existing2: liveOr('existing2', dbrInputs.existing2),
			};
	return dbrSums({
		values,
		personaSlots,
		joint: true,
		productObligations: dbrInputs.productObligations,
		thresholdPct,
	}).available;
}
